import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSession } from '../lib/session'
import { useTheme } from '../lib/theme'
import { initials } from './ui'

/**
 * The desktop/tablet chrome for church staff.
 *
 * Not a second app — the screens inside it are the same routes, with the same
 * data logic, that a phone renders inside <Shell>. This only replaces the
 * chrome: a top bar with real tab navigation, an icon rail, and room to lay a
 * screen out in columns instead of one scrolling strip.
 *
 * The mockup this follows carried a Reviewer/Admin toggle in the top bar.
 * That is deliberately NOT built: it was a mockup device for showing both
 * roles in one file, and shipping it would let an admin walk into a reviewer's
 * screen. `church_members.role` is the only thing that decides, and admin and
 * reviewer are separate jobs on purpose. Where a person genuinely holds seats
 * at more than one church, the church switcher below is the honest version of
 * that control — it moves you between memberships you actually hold.
 */

const TABS = {
  reviewer: [
    { to: '/inbox', label: 'Inbox' },
    { to: '/review', label: 'Song review', matchPrefix: '/review' },
    { to: '/admin/endorsements', label: 'Endorsements' },
  ],
  admin: [
    { to: '/admin/people', label: 'People' },
    { to: '/admin/process', label: 'Setup' },
    { to: '/admin/endorsements', label: 'Endorsements' },
  ],
  /* Platform staff. Keyed separately from the two church seats because SPM
     holds no seat at all: `role` is null for them, which left this object
     returning [] and rendered the portal chrome with no navigation in it. */
  platform: [
    { to: '/admin/platform', label: 'Business' },
    { to: '/admin/feedback', label: 'Support' },
    { to: '/admin/requests', label: 'Requests' },
    { to: '/admin/moderation', label: 'Moderation' },
  ],
}

const RAIL = [
  { d: <><path d="M4 4h16v12H7l-3 3V4Z" /></>, label: 'Inbox', to: '/inbox', roles: ['reviewer'] },
  { d: <><circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0" /></>, label: 'People', to: '/admin/people', roles: ['admin'] },
  { d: <><path d="M4 6h16" /><path d="M4 12h10" /><path d="M4 18h13" /></>, label: 'Setup', to: '/admin/process', roles: ['admin'] },
  { d: <><path d="M9 12l2 2 4-4" /><rect x="4" y="4" width="16" height="16" rx="2" /></>, label: 'Endorsements', to: '/admin/endorsements', roles: ['admin', 'reviewer'] },
  { d: <><path d="M9 18V5l11-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="17" cy="16" r="3" /></>, label: 'Your songs', to: '/me', roles: ['admin', 'reviewer'] },
  { d: <><path d="M4 20V10" /><path d="M10 20V4" /><path d="M16 20v-7" /><path d="M21 20H3" /></>, label: 'Business', to: '/admin/platform', roles: ['platform'] },
  { d: <><path d="M4 5h16v11H8l-4 4V5Z" /><path d="M9 10h6" /></>, label: 'Support', to: '/admin/feedback', roles: ['platform'] },
  { d: <><path d="M6 3h9l4 4v14H6V3Z" /><path d="M14 3v5h5" /></>, label: 'Requests', to: '/admin/requests', roles: ['platform'] },
  { d: <><path d="M12 3l7 3.4v6.1c0 4.8-3 7.9-7 9-4-1.1-7-4.2-7-9V6.4L12 3Z" /><path d="m9 12 2.2 2.2L15.4 10" /></>, label: 'Moderation', to: '/admin/moderation', roles: ['platform'] },
]

const Ico = ({ d, size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
)

export default function PortalFrame({ children, title, sub, back, backLabel, aside }) {
  const { user, profile, role, approved, chooseChurch, active, isPlatformOnly } = useSession()
  const nav = useNavigate()
  const { pathname } = useLocation()

  const [, setThemeMode, night] = useTheme()

  /**
   * The field lives on <body>, not on a layer inside this component.
   *
   * It used to be a fixed-position child sized to the viewport, which paints
   * correctly but leaves flat white anywhere it fails to — and "flat white
   * below the fold" is exactly the failure that is hard to tell apart from a
   * layout bug. On body with background-attachment:fixed it stays
   * viewport-anchored while scrolling (same look) but is painted across the
   * whole document at full viewport width, so no bare strip is reachable at
   * any scroll position or window size. Removed on unmount so the phone
   * shell and the landing page are unaffected.
   */
  useEffect(() => {
    document.body.classList.add('portal')
    return () => document.body.classList.remove('portal')
  }, [])
  /* Staff without a seat get the platform set. Checked before `role` rather
     than falling back to it, so a superadmin who ALSO holds a church seat
     keeps that seat's chrome — the same rule session.jsx applies everywhere
     else, and the reason isPlatformOnly is seat-aware in the first place. */
  const seat = isPlatformOnly ? 'platform' : role
  const tabs = TABS[seat] ?? []
  const rail = RAIL.filter((r) => r.roles.includes(seat))

  // Only offer the switcher when there is genuinely somewhere to switch to.
  const otherSeats = approved.filter((m) => m.church_id !== active?.church_id)

  const name = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ')
    || user?.email || ''

  return (
    <div className="pf">
      <header className="pf-top">
        <div className="pf-brandwrap">
          <button className="pf-brand" onClick={() => nav('/')} aria-label="Beseter — home">
            <span className="pf-mark" aria-hidden="true"><span /></span>
            beseter
          </button>
          {/* The seat you are sitting in, stated rather than chosen. */}
          <span className={`pf-seat pf-seat-${seat}`}>
            {seat === 'platform' ? 'Platform'
              : seat === 'admin' ? 'Admin'
              : seat === 'reviewer' ? 'Reviewer' : 'Songwriter'}
          </span>
        </div>

        <nav className="pf-tabs" aria-label="Main">
          {tabs.map((t) => {
            const on = t.matchPrefix ? pathname.startsWith(t.matchPrefix) : pathname === t.to
            // "Song review" is a place you arrive at from the inbox, not a
            // destination you pick — it needs a song id. Shown so the current
            // location is legible, disabled until you are actually in one.
            const reachable = !t.matchPrefix || on
            return (
              <button key={t.to} className={`pf-tab${on ? ' on' : ''}`}
                      disabled={!reachable}
                      onClick={() => reachable && !on && nav(t.to)}
                      aria-current={on ? 'page' : undefined}>
                {t.label}
              </button>
            )
          })}
        </nav>

        <div className="pf-actions">
          {otherSeats.length > 0 && (
            <select className="pf-church" value={active?.church_id ?? ''}
                    onChange={(e) => chooseChurch(e.target.value)}
                    aria-label="Which church you are working in">
              {approved.map((m) => (
                <option key={m.church_id} value={m.church_id}>
                  {m.churches?.name ?? 'Church'} — {m.role}
                </option>
              ))}
            </select>
          )}
          <button className="pf-icon" onClick={() => nav('/notifications')} aria-label="Notifications">
            <Ico d={<><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>} size={15} />
          </button>
          <button className="pf-icon pf-icon-dark" aria-label={night ? 'Switch to day' : 'Switch to night'}
                  onClick={() => setThemeMode(night ? 'day' : 'night')}>
            {night
              ? <Ico d={<><circle cx="12" cy="12" r="4.5" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>} size={14} />
              : <Ico d={<path d="M21 12.8A9 9 0 0 1 11.2 3a7 7 0 1 0 9.8 9.8Z" />} size={14} />}
          </button>
          <button className="pf-ava" onClick={() => nav('/me')} aria-label="You and your settings">
            {initials(name)}
          </button>
        </div>
      </header>

      <div className="pf-body">
        <aside className="pf-rail" aria-label="Sections">
          {rail.map((r) => {
            const on = pathname === r.to
            return (
              <button key={r.to} className={`pf-railbtn${on ? ' on' : ''}`}
                      onClick={() => nav(r.to)} aria-label={r.label}
                      aria-current={on ? 'page' : undefined} title={r.label}>
                <Ico d={r.d} />
              </button>
            )
          })}
        </aside>

        <main className="pf-main">
          {back && (
            <button className="pf-back" onClick={() => nav(back)}>
              <Ico d={<path d="M15 18l-6-6 6-6" />} size={13} /> {backLabel || 'Back'}
            </button>
          )}
          {title && (
            <div className="pf-head">
              <div>
                <h1 className="pf-h1">{title}</h1>
                {sub && <p className="pf-sub">{sub}</p>}
              </div>
              {aside}
            </div>
          )}
          {children}
        </main>
      </div>

      <p className="pf-foot">© Beseter 2026</p>
    </div>
  )
}
